import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { StandardLoader } from './StandardLoader'
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, DialogFooter } from '@duck/ui'
import { TranslationKey, useTar } from '@/modules'

interface GoogleAuthHandlerProps {
  provider: 'google' | 'linkedin';
  errorCode: string;
  error: string;
  token: string;
}
/**
 * Component to handle Google OAuth callback
 * This component is displayed when user is redirected back from Google OAuth
 */
const errorMap: Record<string, TranslationKey> = {
  uninvitedEmail: 'errors.uninvitedEmail',
  invalidCredentials: 'errors.invalidCredentials',
  userNotFound: 'errors.userNotFound',
  invalidToken: 'errors.invalidToken',
  expiredToken: 'errors.expiredToken',
  badRequest: 'errors.badRequest',
  invalid_grant: 'errors.invalidToken', // Map to existing invalidToken
  access_denied: 'errors.invalidCredentials', // Map to existing invalidCredentials
  invalid_state: 'errors.invalidToken', // Map to existing invalidToken
}

export const ProviderAuthHandler = ({ provider, errorCode, error, token }: GoogleAuthHandlerProps) => {
  const navigate = useNavigate()
  const { initializeAuth, clearError } = useAuth()
  const [errorMessage, setErrorMessage] = useState<TranslationKey | null>(null)
  const [isProcessing, setIsProcessing] = useState(true)
  const { t } = useTar()

  useEffect(() => {
    const handleGoogleAuthCallback = async () => {
      try {
        if (errorCode || error) {
          setErrorMessage(errorMap[errorCode] || errorMap[error] || 'errors.authError')
          setIsProcessing(false)
          return
        }

        // Clear any previous errors
        clearError()

        // The backend has already set the authentication cookies and redirected with the token
        // We just need to initialize the auth state from the server
        await initializeAuth()

        // Get the redirect URL or default to home
        const storedUrl = localStorage.getItem('auth_redirect_url') || '/'
        localStorage.removeItem('auth_redirect_url')
        const redirectUrl = storedUrl.startsWith('/') && !storedUrl.startsWith('//')
          ? storedUrl
          : '/'
        // Navigate to the intended page
        navigate({ to: redirectUrl })

      } catch (error) {
        console.error(`#${provider} OAuth client callback error:`, error)

        // Show error dialog instead of navigating away
        setErrorMessage('errors.authError')
        setIsProcessing(false)
      }
    }

    const timer = setTimeout(() => {
      handleGoogleAuthCallback()
    }, 250)

    return () => clearTimeout(timer)
  }, [errorCode, error, token, navigate, initializeAuth, clearError])

  const handleGoHome = () => {
    navigate({ to: '/' })
  }

  return (
    <>
      {isProcessing && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <StandardLoader size="lg" />
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {t('auth.authenticating')}
              </h2>
              <p className="text-muted-foreground">
                {t('auth.authenticatingDescription')}
              </p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!errorMessage} onOpenChange={handleGoHome}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('errors.authErrorTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 flex-1 flex justify-center items-center">
            {/* Error Message */}
            <div className="flex-1 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-error">
                    {t(errorMessage || 'errors.authError')}
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleGoHome}
              variant="outline"
            >
              {t('common.goHome')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
